import { AttachmentApiService, CategoryApiService } from '@/core';
import { CommonStatusValues } from '@/shared';
import { formControl, Validators } from '@al00x/forms';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import {
  APIHelper,
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  CacImageUploaderComponent,
  formBuilder,
} from '@controladad/ng-base';
import { CategoryCreate, CategoryEntity, CategoryUpdate } from '@repo/types';
import { FileType, UploadResult } from '@/core/services/attachment-api.service';

export interface CategoryDialogData {
  category?: CategoryEntity;
}

export type CategoryDialogResult = boolean;

@Component({
  selector: 'feature-category-dialog',
  standalone: true,
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacImageUploaderComponent, ReactiveFormsModule],
  templateUrl: './category-dialog.component.html',
  styleUrl: './category-dialog.component.scss',
})
export class CategoryDialogComponent extends CacBaseDialogComponent<CategoryDialogData, CategoryDialogResult> {
  private readonly categoryApi = inject(CategoryApiService);
  private readonly attachmentApi = inject(AttachmentApiService);

  image = formControl<string | UploadResult>(null, Validators.required);
  color = formControl<string | null>(null, Validators.required);

  form = formBuilder({
    cols: 2,
    inputs: {
      name: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Name',
      },
      priority: {
        control: formControl<number>(undefined, Validators.required),
        label: 'Priority',
        inputType: 'number',
      },
      status: {
        control: formControl<boolean>(true, Validators.required),
        label: 'Enable',
        type: 'switch',
      },

      parentId: {
        control: formControl<number | null>(undefined),
        label: 'Parent',
        type: 'select',
        items: this.categoryApi.getAll().pipe(
          map((categories) =>
            categories
              ?.filter((category) => category.id !== this.data?.category?.id)
              ?.map((category) => ({
                label: category.name,
                value: category.id,
              })),
          ),
        ),
      },
    },
  });

  get options() {
    return {
      fileType: 'Category' as FileType,
    };
  }

  constructor() {
    super();
    const id = this.data?.category?.id;
    if (id) {
      const data = this.data.category!;
      this.form.patchValue({
        name: data.name,
        priority: data.priority,
        status: data.status == 1,
        parentId: data.parent?.id || null,
      });
      this.image.patchValue(data.image.url);
      this.color.patchValue(data.colorCode);
    }
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getValue();
    if (!values) return;

    const id = this.data.category?.id;
    const imagePath = this.getImagePath();

    const createModel: CategoryCreate = {
      ...values,
      status: values.status ? 1 : 0,
      parentId: values.parentId,
      imagePath,
      colorCode: this.color.value!,
    };

    const rawUpdateModel = {
      id: id!,
      imagePath: imagePath,
      name: values.name,
      priority: values.priority,
      status: values.status ? 1 : 0,
      parentId: typeof values.parentId === 'number' ? values.parentId : null,
      colorCode: this.color.value!,
    };
    const updateModel: CategoryUpdate = APIHelper.nestValues(rawUpdateModel);

    (id ? this.categoryApi.updateCategory(updateModel) : this.categoryApi.create(createModel))
      .pipe(e.pipe())
      .subscribe(() => {
        this.submit(true);
      });
  }

  private getImagePath(): string {
    const value = this.image.value;
    if (!value) return '';
    if (typeof value === 'string') return this.data.category?.image?.path!;
    return value.path;
  }
}
